import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Checkbox, Flex, Text } from 'onefaceui-react-components';

import { getResourceTo } from 'react/utils/utils';
import DownloadIcon from 'static/images/photoAlbum/download-active.svg';
import DownloadInfo from 'static/images/photoAlbum/info.svg';

function PhotoAlbumSelectionBar(props) {
  const {
    onChangeSelectAll,
    onClickDownload,
    albums,
    isAllChecked,
    showDownloadButton,
    isLoadingDownloadButton,
    showLabelInfo
  } = props;

  const { countAlbums, countPhotos } = useMemo(() => {
    const validAlbums = Object.values(albums).filter(Boolean);

    return {
      countAlbums: validAlbums?.length ?? 0,
      countPhotos: validAlbums.reduce((acc, curr) => acc + curr.length, 0)
    };
  }, [albums]);

  const handleChangeSelectAll = useCallback(
    (isChecked) => {
      onChangeSelectAll(isChecked);
    },
    [onChangeSelectAll]
  );

  const generateCheckbox = useCallback(
    () => (
      <Checkbox
        checked={isAllChecked}
        onChange={handleChangeSelectAll}
        className="oneface-checkbox"
        label={getResourceTo('galleryPopup.selectall')}
      />
    ),
    [isAllChecked, handleChangeSelectAll]
  );

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      className="selection-container"
      dataTestId="selection-container"
    >
      <Flex alignItems="center" gap={8} className="selection-status">
        <Text size="xs" type="secondary">
          <span data-testid="selection-selected-albums">
            {countAlbums} {getResourceTo('galleryPopup.albums.countselected')},{' '}
          </span>
          <span data-testid="selection-selected-photos">
            {countPhotos} {getResourceTo('galleryPopup.countselected')}
          </span>
        </Text>
        {generateCheckbox()}
      </Flex>
      {showDownloadButton && (
        <Button
          dataTestId="photo-album-download-button"
          className="photo-album-download-button"
          variant="secondary"
          onClick={() => onClickDownload()}
          loading={isLoadingDownloadButton}
        >
          <img src={DownloadIcon} alt="Upload" />
          {getResourceTo('galleryPopup.downloadbutton')}
        </Button>
      )}
      {showLabelInfo && (
        <Text
          dataTestId="photo-album-label-info"
          className="selection-label-info"
          variant="neutral"
          size="xxs"
        >
          <img src={DownloadInfo} alt="Info Icon" />
          {getResourceTo('galleryPopup.selectionInfo')}
        </Text>
      )}
    </Flex>
  );
}

PhotoAlbumSelectionBar.defaultProps = {
  showDownloadButton: true,
  showLabelInfo: false,
  isLoadingDownloadButton: false
};

PhotoAlbumSelectionBar.propTypes = {
  onChangeSelectAll: PropTypes.func.isRequired,
  onClickDownload: PropTypes.func.isRequired,
  albums: PropTypes.object.isRequired,
  isAllChecked: PropTypes.bool.isRequired,
  showDownloadButton: PropTypes.bool,
  showLabelInfo: PropTypes.bool,
  isLoadingDownloadButton: PropTypes.bool
};

export default PhotoAlbumSelectionBar;
